// src/components/notify/console/NotifyTemplatesPanel.js
// Templates: built-in (read-only) + your own (create / edit / duplicate / delete), variables
// detected from {{placeholders}}, live render with sample values, "Use" → composer.
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
    Badge, Box, Button, Flex, FormControl, FormHelperText, FormLabel, HStack, Icon, IconButton, Input, Modal, ModalBody,
    ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Stack, Switch, Text,
    Textarea, Tooltip, useColorModeValue, useDisclosure, useToast, Wrap, WrapItem,
} from '@chakra-ui/react';
import { MdAdd, MdContentCopy, MdDeleteOutline, MdEdit, MdLock, MdSend } from 'react-icons/md';
import * as api from '../../../services/notifyApi';
import { CATEGORY_META, PRIORITY_META, categoryMeta, priorityMeta } from '../notifyFormat';

const PLACEHOLDER = /\{\{\s*([\w.[\]-]+)\s*\}\}/g;
const varsOf = (...t) => [...new Set(t.join(' ').match(PLACEHOLDER)?.map(m => m.replace(/[{}\s]/g, '')) || [])];
const sample = (text) => String(text || '').replace(PLACEHOLDER, (_, v) => `‹${v}›`);
const BLANK = { id: null, name: '', key: '', title: '', body: '', category: 'announcements', priority: 'normal', requiresAck: false, actionUrl: '' };

export default function NotifyTemplatesPanel({ templates, loading, canEdit, onUse, onChanged }) {
    const toast = useToast();
    const panel = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const previewBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const editor = useDisclosure();
    const [draft, setDraft] = useState(BLANK);
    const [saving, setSaving] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const cancelRef = useRef(null);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const openNew = useCallback(() => { setDraft(BLANK); editor.onOpen(); }, [editor]);
    const openEdit = useCallback((t) => {
        setDraft({ id: t.system ? null : t.id, name: t.system ? `${t.name} (copy)` : t.name, key: t.system ? '' : (t.key?.startsWith('tpl_') ? '' : t.key), title: t.title, body: t.body, category: t.category, priority: t.priority, requiresAck: t.requires_ack, actionUrl: t.action_url || '' });
        editor.onOpen();
    }, [editor]);
    const duplicate = useCallback((t) => { openEdit({ ...t, system: true }); }, [openEdit]);

    const save = useCallback(async () => {
        setSaving(true);
        try {
            const body = { name: draft.name, key: draft.key || undefined, title: draft.title, body: draft.body, category: draft.category, priority: draft.priority, requiresAck: draft.requiresAck, actionUrl: draft.actionUrl || undefined };
            if (draft.id) await api.updateTemplate(draft.id, body); else await api.createTemplate(body);
            toast({ status: 'success', title: draft.id ? 'Template updated' : 'Template created', duration: 2500 });
            editor.onClose();
            onChanged?.();
        } catch (e) {
            toast({ status: 'error', title: 'Could not save template', description: e.message });
        } finally {
            if (isMounted.current) setSaving(false);
        }
    }, [draft, toast, editor, onChanged]);

    const confirmDelete = useCallback(async () => {
        const t = toDelete;
        setToDelete(null);
        try {
            await api.deleteTemplate(t.id);
            toast({ status: 'success', title: 'Template deleted', duration: 2000 });
            onChanged?.();
        } catch (e) { toast({ status: 'error', title: 'Could not delete', description: e.message }); }
    }, [toDelete, toast, onChanged]);

    const draftVars = useMemo(() => varsOf(draft.title, draft.body), [draft.title, draft.body]);
    const groups = useMemo(() => [
        { label: 'Your templates', items: (templates || []).filter(t => !t.system) },
        { label: 'Built-in', items: (templates || []).filter(t => t.system) },
    ], [templates]);

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={3}>
                <Text fontSize="sm" color={muted}>Reusable messages with {'{{variables}}'}. Built-in templates are used by FlowSense automation and AI.</Text>
                {canEdit && <Button size="sm" colorScheme="purple" leftIcon={<MdAdd />} onClick={openNew}>New template</Button>}
            </Flex>
            {loading && !templates?.length ? <Text color={muted}>Loading templates…</Text> : groups.map(g => g.items.length > 0 && (
                <Box key={g.label} mb={5}>
                    <Text fontSize="xs" fontWeight="700" color={muted} textTransform="uppercase" mb={2}>{g.label} ({g.items.length})</Text>
                    <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
                        {g.items.map(t => {
                            const cat = categoryMeta(t.category);
                            const pr = priorityMeta(t.priority);
                            return (
                                <Box key={t.key} bg={panel} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} opacity={t.is_active === false ? 0.6 : 1}>
                                    <Flex justify="space-between" align="flex-start" gap={2}>
                                        <HStack spacing={2} minW={0}><Icon as={cat.icon} color={`${cat.color}.500`} /><Text fontWeight="700" fontSize="sm" noOfLines={1}>{t.name}</Text></HStack>
                                        {t.system && <Tooltip label="Built-in — duplicate to customise" hasArrow><span><Icon as={MdLock} color={muted} aria-label="Built-in" /></span></Tooltip>}
                                    </Flex>
                                    <Text fontSize="xs" color={muted} mt={0.5}>{t.key}{t.usage_count ? ` · used ${t.usage_count}×` : ''}</Text>
                                    <Box bg={previewBg} borderRadius="md" p={2} mt={2}>
                                        <Text fontSize="sm" fontWeight="600" noOfLines={1}>{sample(t.title)}</Text>
                                        <Text fontSize="xs" color={muted} noOfLines={2}>{sample(t.body)}</Text>
                                    </Box>
                                    <Wrap spacing={1} mt={2}>
                                        <WrapItem><Badge colorScheme={cat.color} variant="subtle" fontSize="2xs">{cat.label}</Badge></WrapItem>
                                        {t.priority !== 'normal' && <WrapItem><Badge colorScheme={pr.color} fontSize="2xs">{pr.label}</Badge></WrapItem>}
                                        {t.requires_ack && <WrapItem><Badge colorScheme="red" variant="outline" fontSize="2xs">Ack</Badge></WrapItem>}
                                        {(t.variables || []).slice(0, 4).map(v => <WrapItem key={v}><Badge variant="outline" fontSize="2xs">{v}</Badge></WrapItem>)}
                                    </Wrap>
                                    <HStack mt={3} spacing={1}>
                                        <Button size="xs" colorScheme="purple" leftIcon={<MdSend />} onClick={() => onUse?.(t)}>Use</Button>
                                        {canEdit && <Tooltip label="Duplicate" hasArrow><IconButton size="xs" variant="ghost" icon={<MdContentCopy />} aria-label={`Duplicate ${t.name}`} onClick={() => duplicate(t)} /></Tooltip>}
                                        {canEdit && !t.system && <Tooltip label="Edit" hasArrow><IconButton size="xs" variant="ghost" icon={<MdEdit />} aria-label={`Edit ${t.name}`} onClick={() => openEdit(t)} /></Tooltip>}
                                        {canEdit && !t.system && <Tooltip label="Delete" hasArrow><IconButton size="xs" variant="ghost" colorScheme="red" icon={<MdDeleteOutline />} aria-label={`Delete ${t.name}`} onClick={() => setToDelete(t)} /></Tooltip>}
                                    </HStack>
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                </Box>
            ))}

            <Modal isOpen={editor.isOpen} onClose={editor.onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{draft.id ? 'Edit template' : 'New template'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Stack spacing={3}>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <FormControl isRequired><FormLabel fontSize="sm">Name</FormLabel><Input size="sm" value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} /></FormControl>
                                <FormControl><FormLabel fontSize="sm">Key (optional)</FormLabel><Input size="sm" value={draft.key} onChange={(e) => setDraft(d => ({ ...d, key: e.target.value }))} placeholder="hydrant_flush" /><FormHelperText fontSize="xs">Lets code send it with template: 'your_key'.</FormHelperText></FormControl>
                            </SimpleGrid>
                            <FormControl isRequired><FormLabel fontSize="sm">Title</FormLabel><Input size="sm" value={draft.title} onChange={(e) => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="Hydrant flushing in {{area}}" /></FormControl>
                            <FormControl isRequired><FormLabel fontSize="sm">Message</FormLabel><Textarea size="sm" rows={4} value={draft.body} onChange={(e) => setDraft(d => ({ ...d, body: e.target.value }))} placeholder="Expect low pressure {{when}}." /></FormControl>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <FormControl><FormLabel fontSize="sm">Topic</FormLabel>
                                    <Select size="sm" value={draft.category} onChange={(e) => setDraft(d => ({ ...d, category: e.target.value }))}>{Object.entries(CATEGORY_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</Select>
                                </FormControl>
                                <FormControl><FormLabel fontSize="sm">Priority</FormLabel>
                                    <Select size="sm" value={draft.priority} onChange={(e) => setDraft(d => ({ ...d, priority: e.target.value }))}>{Object.entries(PRIORITY_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</Select>
                                </FormControl>
                            </SimpleGrid>
                            <FormControl><FormLabel fontSize="sm">Link (optional)</FormLabel><Input size="sm" value={draft.actionUrl} onChange={(e) => setDraft(d => ({ ...d, actionUrl: e.target.value }))} placeholder="/admin/roads" /></FormControl>
                            <Flex justify="space-between" align="center"><Text fontSize="sm">Require acknowledgement</Text><Switch colorScheme="red" isChecked={draft.requiresAck} onChange={(e) => setDraft(d => ({ ...d, requiresAck: e.target.checked }))} aria-label="Require acknowledgement" /></Flex>
                            {draftVars.length > 0 && (
                                <HStack spacing={1} flexWrap="wrap"><Text fontSize="xs" color={muted}>Variables:</Text>{draftVars.map(v => <Badge key={v} variant="outline">{v}</Badge>)}</HStack>
                            )}
                            <Box bg={previewBg} borderRadius="md" p={3}>
                                <Text fontSize="xs" color={muted} mb={1}>Preview</Text>
                                <Text fontWeight="700" fontSize="sm">{sample(draft.title) || '—'}</Text>
                                <Text fontSize="sm" whiteSpace="pre-line">{sample(draft.body)}</Text>
                            </Box>
                        </Stack>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={editor.onClose}>Cancel</Button>
                        <Button colorScheme="purple" onClick={save} isLoading={saving} isDisabled={!draft.name.trim() || !draft.title.trim() || !draft.body.trim()}>Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <AlertDialog isOpen={Boolean(toDelete)} leastDestructiveRef={cancelRef} onClose={() => setToDelete(null)} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader>Delete “{toDelete?.name}”?</AlertDialogHeader>
                        <AlertDialogBody>Notifications already sent are not affected.</AlertDialogBody>
                        <AlertDialogFooter gap={2}>
                            <Button ref={cancelRef} onClick={() => setToDelete(null)}>Cancel</Button>
                            <Button colorScheme="red" onClick={confirmDelete}>Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
}
